import { useMemo } from 'preact/hooks';

export interface UseUrlContentIdOptions {
  defaultId?: string;
  validIds?: string[];
  paramName?: string;
  url?: string;
}

/**
 * Parse content ID from URL (query param, path, or hash)
 */
function parseContentId(urlString: string, validIds?: string[], paramName = 'content'): string | null {
  const isValid = (id: string) => !validIds || validIds.includes(id);
  
  try {
    const url = new URL(urlString);
    
    // 1. Query param: ?content=green
    const queryParam = url.searchParams.get(paramName);
    if (queryParam && isValid(queryParam)) return queryParam;
    
    // 2. Path: /green
    const pathSegment = url.pathname.split('/').filter(s => s)[0];
    if (pathSegment && isValid(pathSegment)) return pathSegment;
    
    // 3. Hash: #green
    const hash = url.hash.replace('#', '');
    if (hash && isValid(hash)) return hash;
    
    return null;
  } catch {
    return null;
  }
}

/**
 * Hook to get content ID from URL
 */
export function useUrlContentId(options: UseUrlContentIdOptions = {}): string | null {
  const { defaultId, validIds, paramName = 'content', url } = options;
  
  return useMemo(() => {
    const urlToParse = url || window.location.href;
    return parseContentId(urlToParse, validIds, paramName) || defaultId || null;
  }, [url, paramName, validIds?.join(','), defaultId]);
}
